import Vue from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	COMPANY_USER_ROLE_PERMISSION,
	COMPANY_USER_ROLE_PERMISSION_PASSING_VAL,
	COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT,
	CompanyUserRole,
	CompanyUserRolePermission,
	Runnit,
	RunnitNodeDef,
	TeamUserRoleDef,
	TEAM_USER_ROLE_PERMISSION,
	TEAM_USER_ROLE_PERMISSION_PASSING_VAL,
	TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT,
	Team,
	TeamUserRole,
	TeamUserRolePermission,
	resolveTokenLimitMax,
} from '@run-diffusion/shared';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { isRunnitOwnerChangeAllowed } from '@/views/Runnits/RunnitManageAccessDialog/runnitOwnership';
import { setRunnitsOwnerSelection } from '@/utils';
import { RunnitsOwnerSelection } from '@/store';

export const TeamUserMixin = Vue.extend({
	computed: {
		...mapGetters([
			'companyClubOfferBenefits',
		]),
		...mapState([
			'user',
			'loadingUser',
			'team',
			'loadingTeam',
			'companyUserRole',
			'loadingCompanyUserRole',
			'teamUserRole',
			'loadingTeamUserRole',
			'runnitState',
		]),
		isOnboardingTeam () {
			return !!this.team?.isOnboarding;
		},
		isTeamActive () {
			return !!this.team?.isActive;
		},

		// Company-level permission checks
		getCompanyPermissionOverridePrechecks (): { useNonPassingVal: boolean, usePassingVal: boolean, useNonQualifyingPlanDefault: boolean } {
			return {
				// Loading state overrides to non-passing val
				useNonPassingVal: !!(this.loadingUser || this.loadingTeam || this.loadingCompanyUserRole),
				// RunDiffusion admin always allowed
				usePassingVal: !!this.user?.isAdmin,
				// Non-qualifying plan defaults (i.e. personal plans do not qualify to read company role permissions)
				useNonQualifyingPlanDefault: !!(
					!this.team?.id ||
					this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER
				),
			};
		},
		hasManageCompanyPermission (): boolean {
			return this.checkManageCompanyPermission(this.team, this.companyUserRole);
		},
		hasManageApiTokensPermission (): boolean {
			return this.checkManageApiTokensPermission(this.team, this.companyUserRole);
		},
		hasManageCompanyTeamRolesPermission (): boolean {
			return this.checkManageCompanyTeamRolesPermission(this.team, this.companyUserRole);
		},
		hasViewCompanyReportsPermission (): boolean {
			return this.checkViewCompanyReportsPermission(this.team, this.companyUserRole);
		},
		hasManageTeamsPermission (): boolean {
			return this.checkManageTeamsPermission(this.team, this.companyUserRole);
		},
		hasManageCompanyPromptLibraryPermission (): boolean {
			return this.checkManageCompanyPromptLibraryPermission(this.team, this.companyUserRole);
		},

		// Team-level permission checks
		getTeamPermissionOverridePrechecks (): { useNonPassingVal: boolean, usePassingVal: boolean, useNonQualifyingPlanDefault: boolean } {
			return {
				// Loading state overrides to non-passing val
				useNonPassingVal: !!(this.loadingUser || this.loadingTeam || this.loadingCompanyUserRole || this.loadingTeamUserRole),
				// RunDiffusion admin always allowed
				usePassingVal: !!this.user?.isAdmin,
				// Non-qualifying plan defaults (i.e. personal & team plans do not qualify to read team role permissions)
				useNonQualifyingPlanDefault: !!(
					!this.team?.id ||
					this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER ||
					!this.companyClubOfferBenefits.isEnterprise
				),
			};
		},
		hasRdAdminPermission (): boolean {
			return this.checkRdAdminPermission(this.team, this.teamUserRole);
		},
		hasUseRdPermission (): boolean {
			return this.checkUseRdPermission(this.team, this.teamUserRole);
		},
		hasGenerateRunnitPermission (): boolean {
			return this.checkGenerateRunnitPermission(this.team, this.teamUserRole);
		},
		hasUsePluginsPermission (): boolean {
			return this.checkUsePluginsPermission(this.team, this.teamUserRole);
		},
		// One place for the plugins surface's name, used by the page title, the
		// nav item, and the breadcrumb.
		pluginsSurfaceLabel (): string {
			return 'Plugins & APIs';
		},
		hasBatchGenerationPermission (): boolean {
			return this.checkBatchGenerationPermission(this.team, this.teamUserRole);
		},
		hasTokenLimitPermission (): boolean {
			return this.checkTokenLimitPermission(this.team, this.teamUserRole);
		},
		hasTokenCooldownPermission (): boolean {
			return this.checkTokenCooldownPermission(this.team, this.teamUserRole);
		},
		tokenLimitApplyToTotalRun (): boolean {
			if (!this.teamUserRole?.teamId) return false;
			if (!this.teamUserRole || this.teamUserRole.teamId !== this.team?.id) return false;
			return !!this.teamUserRole.roleDef?.tokenLimitSettings?.applyToTotalRun;
		},
		hasViewTrainPermission (): boolean {
			return this.checkViewTrainPermission(this.team, this.teamUserRole);
		},
		hasShareRunnitAssetsPermission (): boolean {
			return this.checkShareRunnitAssetsPermission(this.team, this.teamUserRole);
		},
		hasManageAllGenerationsPermission (): boolean {
			return this.checkManageAllGenerationsPermission(this.team, this.teamUserRole);
		},
		hasManagePromptLibraryPermission (): boolean {
			return this.checkManagePromptLibraryPermission(this.team, this.teamUserRole);
		},
		hasManageTeamMembersPermission (): boolean {
			return this.checkManageTeamMembersPermission(this.team, this.teamUserRole);
		},
		hasShowTokenBalancePermission (): boolean {
			return this.checkShowTokenBalancePermission(this.team, this.teamUserRole);
		},
		hasNumResultsLimitPermission (): boolean {
			return this.checkNumResultsLimitPermission(this.team, this.teamUserRole);
		},
		numResultsLimitMax (): number | null {
			return this.getNumResultsLimitMax(this.team, this.teamUserRole);
		},
		/** Company uses SCIM IdP-only provisioning: no in-app member/role changes. */
		scimProvisioningLocksMembership (): boolean {
			const sso = this.team?.company?.sso as
				| { scimProvisioningEnabled?: boolean }
				| undefined;
			return !!sso?.scimProvisioningEnabled;
		},
		hasBlockNodeDefsPermission (): boolean {
			return this.checkBlockNodeDefsPermission(this.team, this.teamUserRole);
		},
	},
	methods: {
		/**
		 * Whether the caller can hand this board to another member of its team, from its Access
		 * Settings. Only with MANAGE_ALL_GENERATIONS, and only in team mode: see
		 * isRunnitOwnerChangeAllowed for the whole rule.
		 */
		canChangeRunnitOwner (runnit: Runnit | null): boolean {
			return isRunnitOwnerChangeAllowed(runnit, {
				teamId: this.runnitState?.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
					? (this.team?.id || null)
					: null,
				isAdmin: !!this.user?.isAdmin,
				hasManageAllGenerationsPermission: !!this.hasManageAllGenerationsPermission,
			});
		},
		...mapActions([
			'updateRunnitState',
		]),

		// Company-level permission checks
		/**
		 * This is a precheck to see if the user has the permission to manage the company.
		 * This is similar to a Super Admin Role, meaning they access everything within the company.
		 * @returns true if the user has the permission to manage the company, false otherwise
		 */
		/**
		 * Whether the user may reach the API section: creating and regenerating
		 * company API access tokens, and deleting any member's. Those tokens can
		 * spend company tokens, so this is gated separately from general company
		 * management rather than riding on MANAGE_COMPANY.
		 */
		checkManageApiTokensPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_API_TOKENS];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_API_TOKENS];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.MANAGE_API_TOKENS];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.MANAGE_API_TOKENS]
			);
		},
		/**
		 * Shares prompts with everyone in the company's prompt library, and edits or removes any
		 * prompt shared with the company or one of its teams. MANAGE_COMPANY doesn't include it.
		 */
		checkManageCompanyPromptLibraryPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_PROMPT_LIBRARY];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_PROMPT_LIBRARY];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_PROMPT_LIBRARY];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_PROMPT_LIBRARY]
			);
		},
		checkManageCompanyPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to manage the company team roles.
		 * This allows creating, editing, and deleting team roles within the company.
		 * @returns true if the user has the permission to manage the company team roles, false otherwise
		 */
		checkManageCompanyTeamRolesPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to view the company reports.
		 * This allows the user to view the company reports within the company.
		 * @returns true if the user has the permission to view the company reports, false otherwise
		 */
		checkViewCompanyReportsPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to manage the teams within the company.
		 * This allows creating, editing, and deleting teams, team members, and role associated to the members within the company.
		 * @returns true if the user has the permission to manage the teams within the company, false otherwise
		 */
		checkManageTeamsPermission (team: Team, companyUserRole: CompanyUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getCompanyPermissionOverridePrechecks;
			if (useNonPassingVal) return !COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_TEAMS];
			if (usePassingVal) return !!COMPANY_USER_ROLE_PERMISSION_PASSING_VAL[COMPANY_USER_ROLE_PERMISSION.MANAGE_TEAMS];
			if (useNonQualifyingPlanDefault) return !!COMPANY_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[COMPANY_USER_ROLE_PERMISSION.MANAGE_TEAMS];
			return !!(
				companyUserRole &&
				companyUserRole.userId === this.user?.id &&
				companyUserRole.companyId === team.companyId &&
				companyUserRole.roleDef?.permissions?.[COMPANY_USER_ROLE_PERMISSION.MANAGE_TEAMS]
			);
		},

		// Team-level permission checks
		/**
		 * This is a precheck to see if the user has the permission to manage the RD platform.
		 * This allows the user to manage the RD platform within the team.
		 * @returns true if the user has the permission to manage the RD platform, false otherwise
		 */
		checkRdAdminPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.RD_ADMIN];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.RD_ADMIN];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.RD_ADMIN];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.RD_ADMIN]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to use the RD platform.
		 * This allows the user to use the RD platform within the team.
		 * @returns true if the user has the permission to use the RD platform, false otherwise
		 */
		checkUseRdPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.USE_RD];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.USE_RD];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.USE_RD];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.USE_RD]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to generate on the Runnit platform.
		 * @returns true if the user has the permission to generate Runnit, false otherwise
		 */
		checkGenerateRunnitPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.GENERATE_RUNNIT];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.GENERATE_RUNNIT];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.GENERATE_RUNNIT];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.GENERATE_RUNNIT]
			);
		},
		/**
		 * Precheck for plugin usage (USE_PLUGINS). When ON, the role may act through
		 * the plugin kinds listed in pluginAccessSettings.pluginKinds; when OFF, all
		 * plugins are denied for the role.
		 * @returns true if the user's team role has USE_PLUGINS, false otherwise
		 */
		checkUsePluginsPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.USE_PLUGINS];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.USE_PLUGINS];
			// Personal & non-enterprise team plans get the map default (USE_PLUGINS: true, ADR-0021).
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.USE_PLUGINS];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.USE_PLUGINS]
			);
		},
		// True if the given plugin kind (PLUGIN_KIND value, e.g. 'photoshop') is
		// enabled on the current user's team role. RD admins see all kinds;
		// personal & non-enterprise team plans allow every kind incl. future ones
		// (ADR-0021); enterprise uses optInAll or explicit pluginKinds.
		isPluginKindEnabled (pluginKind: string): boolean {
			if (this.user?.isAdmin) return true;
			const { useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonQualifyingPlanDefault) return true;
			const pas = this.teamUserRole?.roleDef?.pluginAccessSettings;
			if (pas?.optInAll) return true;
			return pas?.pluginKinds?.[pluginKind] === true;
		},
		/**
		 * Precheck for batch generation (e.g. batch wizard / queue multiple runs under one batch id).
		 * @returns true if the user may use batch generation for the team, false otherwise
		 */
		checkBatchGenerationPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.BATCH_GENERATION];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.BATCH_GENERATION];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.BATCH_GENERATION];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.BATCH_GENERATION]
			);
		},
		/**
		 * This is a precheck to see if the user has a token limit per generation.
		 * If the user has a token limit per generation, they will see a token limit warning when they generate.
		 * @returns true if the user has a token limit per generation, false otherwise
		 */
		checkTokenLimitPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT]
			);
		},
		checkTokenCooldownPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.TOKEN_COOLDOWN];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.TOKEN_COOLDOWN];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.TOKEN_COOLDOWN];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.TOKEN_COOLDOWN]
			);
		},
		/**
		 * The token cap that applies to `nodeDef`, in hundredths: the role's
		 * per-media-type override when the tool carries that MEDIA tag, else the
		 * role's master cap. Null when the role sets neither, which the run
		 * surfaces treat as a misconfigured role rather than as "no limit".
		 *
		 * There is deliberately no master-cap-only accessor beside this one: a
		 * surface gating a generation must resolve against the tool it is gating,
		 * or it will disagree with what the server enforces. Pass `null` for
		 * `nodeDef` to ask for the master cap on purpose.
		 *
		 * Resolution is the shared `resolveTokenLimitMax`, the same function the
		 * run paths in `firebase/functions` enforce with.
		 */
		getTokenLimitMaxForNodeDef (team: Team, teamUserRole: TeamUserRole, nodeDef: RunnitNodeDef | null): number | null {
			if (!teamUserRole?.teamId) return null;
			if (!teamUserRole || teamUserRole.teamId !== team?.id) return null;
			return resolveTokenLimitMax(teamUserRole.roleDef?.tokenLimitSettings, nodeDef?.tags);
		},
		/**
		 * This is a precheck to see if the user has the permission to view the train page.
		 * This allows the user to view the train page within the team.
		 * @returns true if the user has the permission to view the train page, false otherwise
		 */
		checkViewTrainPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to share Runnit assets.
		 * This allows the user to share Runnit assets within the team.
		 * @returns true if the user has the permission to share Runnit assets, false otherwise
		 */
		checkShareRunnitAssetsPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.SHARE_RUNNIT_ASSETS];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.SHARE_RUNNIT_ASSETS];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.SHARE_RUNNIT_ASSETS];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.SHARE_RUNNIT_ASSETS]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to manage all generations created on the team account.
		 * This allows the user to manage all generations created on the team account.
		 * @returns true if the user has the permission to manage all generations created on the team account, false otherwise
		 */
		checkManageAllGenerationsPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_ALL_GENERATIONS];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_ALL_GENERATIONS];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.MANAGE_ALL_GENERATIONS];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.MANAGE_ALL_GENERATIONS]
			);
		},
		/**
		 * Shares prompts with everyone in the team's prompt library, and edits or removes any prompt
		 * shared with the team. On by default off enterprise plans, where there are no roles to grant it.
		 */
		checkManagePromptLibraryPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_PROMPT_LIBRARY];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_PROMPT_LIBRARY];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.MANAGE_PROMPT_LIBRARY];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.MANAGE_PROMPT_LIBRARY]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to manage team members.
		 * This allows the user to add, remove, and modify team members and their roles.
		 * @returns true if the user has the permission to manage team members, false otherwise
		 */
		checkManageTeamMembersPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_TEAM_MEMBERS];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.MANAGE_TEAM_MEMBERS];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.MANAGE_TEAM_MEMBERS];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.MANAGE_TEAM_MEMBERS]
			);
		},
		/**
		 * This is a precheck to see if the user has the permission to see the team's token balance.
		 * @returns true if the user has the permission to see the team's token balance, false otherwise
		 */
		checkShowTokenBalancePermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.SHOW_TOKEN_BALANCE];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.SHOW_TOKEN_BALANCE];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.SHOW_TOKEN_BALANCE];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.SHOW_TOKEN_BALANCE]
			);
		},
		checkNumResultsLimitPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.NUM_RESULTS_LIMIT];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.NUM_RESULTS_LIMIT];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.NUM_RESULTS_LIMIT];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.NUM_RESULTS_LIMIT]
			);
		},
		checkBlockNodeDefsPermission (team: Team, teamUserRole: TeamUserRole): boolean {
			const { useNonPassingVal, usePassingVal, useNonQualifyingPlanDefault } = this.getTeamPermissionOverridePrechecks;
			if (useNonPassingVal) return !TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.BLOCK_NODE_DEFS];
			if (usePassingVal) return !!TEAM_USER_ROLE_PERMISSION_PASSING_VAL[TEAM_USER_ROLE_PERMISSION.BLOCK_NODE_DEFS];
			if (useNonQualifyingPlanDefault) return !!TEAM_USER_ROLE_PERMISSION_NON_QUALIFYING_PLAN_DEFAULT[TEAM_USER_ROLE_PERMISSION.BLOCK_NODE_DEFS];
			return !!(
				teamUserRole &&
				teamUserRole.userId === this.user?.id &&
				teamUserRole.teamId === team.id &&
				teamUserRole.roleDef?.permissions?.[TEAM_USER_ROLE_PERMISSION.BLOCK_NODE_DEFS]
			);
		},
		/**
		 * Precheck for the max numResults allowed when the user has NUM_RESULTS_LIMIT permission.
		 * @returns the max numResults for the user, null if not set or invalid
		 */
		getNumResultsLimitMax (team: Team, teamUserRole: TeamUserRole): number | null {
			if (!teamUserRole?.teamId) return null;
			if (!teamUserRole || teamUserRole.teamId !== team.id) return null;
			const max: number = teamUserRole.roleDef?.numResultsLimitSettings?.max || 0;
			return max > 0 ? max : null;
		},
		// Helper method that will route a Company permission value to the computed value in the mixin
		hasCompanyUserRolePermission (permission: CompanyUserRolePermission): boolean {
			switch (permission) {
				case COMPANY_USER_ROLE_PERMISSION.VIEW_COMPANY_REPORTS:
					return this.hasViewCompanyReportsPermission;
				case COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY:
					return this.hasManageCompanyPermission;
				case COMPANY_USER_ROLE_PERMISSION.MANAGE_TEAMS:
					return this.hasManageTeamsPermission;
				case COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_TEAM_ROLES:
					return this.hasManageCompanyTeamRolesPermission;
				case COMPANY_USER_ROLE_PERMISSION.MANAGE_COMPANY_PROMPT_LIBRARY:
					return this.hasManageCompanyPromptLibraryPermission;
				default: return false;
			}
		},
		// Helper method that will route a Team permission value to the computed value in the mixin
		hasTeamUserRolePermission (permission: TeamUserRolePermission): boolean {
			switch (permission) {
				case TEAM_USER_ROLE_PERMISSION.GENERATE_RUNNIT:
					return this.hasGenerateRunnitPermission;
				case TEAM_USER_ROLE_PERMISSION.BATCH_GENERATION:
					return this.hasBatchGenerationPermission;
				case TEAM_USER_ROLE_PERMISSION.TOKEN_LIMIT:
					return this.hasTokenLimitPermission;
				case TEAM_USER_ROLE_PERMISSION.TOKEN_COOLDOWN:
					return this.hasTokenCooldownPermission;
				case TEAM_USER_ROLE_PERMISSION.NUM_RESULTS_LIMIT:
					return this.hasNumResultsLimitPermission;
				case TEAM_USER_ROLE_PERMISSION.BLOCK_NODE_DEFS:
					return this.hasBlockNodeDefsPermission;
				case TEAM_USER_ROLE_PERMISSION.VIEW_TRAIN:
					return this.hasViewTrainPermission;
				case TEAM_USER_ROLE_PERMISSION.SHARE_RUNNIT_ASSETS:
					return this.hasShareRunnitAssetsPermission;
				case TEAM_USER_ROLE_PERMISSION.MANAGE_ALL_GENERATIONS:
					return this.hasManageAllGenerationsPermission;
				case TEAM_USER_ROLE_PERMISSION.MANAGE_PROMPT_LIBRARY:
					return this.hasManagePromptLibraryPermission;
				case TEAM_USER_ROLE_PERMISSION.RD_ADMIN:
					return this.hasRdAdminPermission;
				case TEAM_USER_ROLE_PERMISSION.USE_RD:
					return this.hasUseRdPermission;
				case TEAM_USER_ROLE_PERMISSION.MANAGE_TEAM_MEMBERS:
					return this.hasManageTeamMembersPermission;
				case TEAM_USER_ROLE_PERMISSION.SHOW_TOKEN_BALANCE:
					return this.hasShowTokenBalancePermission;
				default: return false;
			}
		},

		/**
		 * Returns true if the given RunnitNodeDef should be hidden for the current team user based on
		 * their role's BLOCK_NODE_DEFS permission and restrictNodeDefTagsSettings.
		 *
		 * A tool is blocked when BOTH conditions hold:
		 *   1. The user's role has BLOCK_NODE_DEFS permission active
		 *   2. The tool matches the blocklist by either:
		 *        - Its ID appearing in restrictNodeDefTagsSettings.nodeDefIds, OR
		 *        - One of its tag IDs appearing in restrictNodeDefTagsSettings.tags
		 *
		 * Admins, non-enterprise teams, and USER-mode owner selections are exempt
		 * (handled transparently by hasBlockNodeDefsPermission via getTeamPermissionOverridePrechecks).
		 */
		isNodeDefBlockedForTeamUser (nodeDef: RunnitNodeDef): boolean {
			if (!this.hasBlockNodeDefsPermission) return false;
			const r: TeamUserRoleDef['restrictNodeDefTagsSettings'] = this.teamUserRole?.roleDef?.restrictNodeDefTagsSettings;
			if (!r) return false;
			if (r.nodeDefIds?.includes(nodeDef.id)) return true;
			const blockedTagIds: Record<string, true> = r.tags || {};
			return Object.keys(blockedTagIds).some(
				(tagId: string) => blockedTagIds[tagId] && nodeDef.tags?.[tagId],
			);
		},

		/**
		 * Switches the Runnits owner selection between team and user mode
		 * @param runnitsOwnerSelection - The owner selection mode to switch to (team or user)
		 */
		switchBetweenTeamAndUser (runnitsOwnerSelection: RunnitsOwnerSelection) {
			setRunnitsOwnerSelection(runnitsOwnerSelection);
			this.updateRunnitState({
				runnitsOwnerSelection,
			});
		},
	},
});
